import { useEffect, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import InfoTip, { EARNINGS_NOTE } from "../InfoTip";
import { Button, Chip, Chips, ErrorText, Note, PageStack, Toolbar } from "../ui";
import { PageProps, formatDate } from "./types";
import { getPartnerReportApi } from "../../../api/apiEndpoints";
import { formatCurrency } from "../../../utils/partnerMetrics";
import { DATE_PRESETS, DatePreset, addDays, downloadCsv, presetRange } from "../../../utils/reportDates";

type Report = Record<string, number | null>;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;

const Metric = styled.div`
  background: #faf8f5;
  border: 1px solid #eee7dd;
  border-radius: 12px;
  padding: 14px 16px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  min-width: 0;

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #111111;
  line-height: 1.1;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: #6b6259;
  margin-top: 6px;
`;

const DateInput = styled.input`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  padding: 7px 10px;
  border: 1.5px solid #e0d8ce;
  border-radius: 10px;
  background: #ffffff;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;
  min-height: 36px;
  color: #111111;
  text-align: left;

  &::-webkit-date-and-time-value {
    text-align: left;
  }

  @media (max-width: 700px) {
    font-size: 16px;
    flex: 1;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b6259;
  flex-wrap: wrap;
`;

interface MetricDef {
  key: string;
  label: string;
  format?: "currency" | "percent" | "decimal";
  info?: string;
}

const SECTIONS: { title: string; subtitle: string; metrics: MetricDef[] }[] = [
  {
    title: "Members",
    subtitle: "People with a checked-in membership that started in this period",
    metrics: [
      { key: "unique_members", label: "Members" },
      { key: "ever_trialed", label: "Trialed" },
      { key: "upgraded", label: "Upgraded after trial" },
      { key: "upgrade_pct", label: "Upgrade rate", format: "percent" },
      {
        key: "direct_premium",
        label: "Joined on upgrade",
        info: "Members who started on your upgrade plan without a trial first.",
      },
      { key: "ever_premium", label: "Ever upgraded" },
      { key: "active_now", label: "Active today" },
    ],
  },
  {
    title: "Billing",
    subtitle: "Billing periods that started in this period, plus fees from classes in this period",
    metrics: [
      { key: "total_payout", label: "Total payout", format: "currency", info: "Membership earnings plus class fees." },
      { key: "studio_payout", label: "Membership earnings", format: "currency", info: EARNINGS_NOTE },
      {
        key: "class_fees",
        label: "Class fees",
        format: "currency",
        info: "No-show ($20.00) and late-cancel ($15.00) fees we collected for your classes in this period, passed to you in full. Includes class drops.",
      },
      { key: "billed_cycles", label: "Billing periods" },
      {
        key: "member_months",
        label: "Member-months",
        info: "Billing periods converted to months of access. A quarterly plan counts as 3, a yearly plan as 12.",
      },
      {
        key: "comped_cycles",
        label: "Comped periods",
        info: "Billing periods where the member paid $0, for example with a promo code.",
      },
    ],
  },
  {
    title: "Visits",
    subtitle: "Classes booked through JackedRabbit that took place in this period",
    metrics: [
      { key: "bookings", label: "Bookings" },
      { key: "attended", label: "Attended" },
      { key: "no_shows", label: "No-shows" },
      { key: "late_cancels", label: "Late cancels" },
      { key: "cancelled", label: "Cancelled" },
      { key: "still_booked", label: "Upcoming" },
      { key: "unique_visitors", label: "Unique visitors" },
      { key: "attended_per_member", label: "Visits per member", format: "decimal" },
    ],
  },
];

const formatMetric = (value: number | null | undefined, format?: MetricDef["format"]) => {
  if (value === null || value === undefined) return "—";
  if (format === "currency") return formatCurrency(value);
  if (format === "percent") return `${value}%`;
  if (format === "decimal") return value.toFixed(1);
  return value.toLocaleString();
};

const downloadReport = (report: Report, studio: string, rangeLabel: string) => {
  const metrics = SECTIONS.flatMap((s) => s.metrics);
  downloadCsv(
    [
      ["Studio", "Period", ...metrics.map((m) => m.label)],
      [studio, rangeLabel, ...metrics.map((m) => report[m.key])],
    ],
    `${studio} report ${rangeLabel}.csv`
  );
};

interface ReportsPageProps extends PageProps {
  studioName?: string;
}

const ReportsPage = ({ businessId, studioName = "Studio" }: ReportsPageProps) => {
  const [preset, setPreset] = useState<DatePreset>("thisMonth");
  const [custom, setCustom] = useState(() => presetRange("last30")!);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = preset === "custom" ? custom : presetRange(preset);
  const invalidCustom = preset === "custom" && (!custom.start || !custom.end || custom.start > custom.end);
  const rangeLabel = range ? `${formatDate(`${range.start}T00:00`)} – ${formatDate(`${range.end}T00:00`)}` : "All time";

  useEffect(() => {
    if (!businessId || invalidCustom) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    // The API takes an exclusive end date.
    const endExclusive = range ? addDays(range.end, 1) : undefined;
    getPartnerReportApi(businessId, range?.start, endExclusive)
      .then((res) => !cancelled && setReport(res.data.report))
      .catch(() => !cancelled && setError("Couldn't load this report. Please try again."));
    return () => {
      cancelled = true;
    };
    // range is derived from preset/custom, which are the real dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, preset, custom.start, custom.end, invalidCustom]);

  return (
    <PageStack>
      <Panel
        title="Studio Report"
        subtitle={rangeLabel}
        action={
          <Button disabled={!report} onClick={() => report && downloadReport(report, studioName, rangeLabel)}>
            <Download size={15} />
            Download CSV
          </Button>
        }
      >
        <Toolbar style={{ marginBottom: preset === "custom" ? 12 : 0 }}>
          <Chips>
            {DATE_PRESETS.map((p) => (
              <Chip key={p.key} $active={preset === p.key} onClick={() => setPreset(p.key)}>
                {p.label}
              </Chip>
            ))}
          </Chips>
        </Toolbar>
        {preset === "custom" && (
          <DateRow>
            <DateInput
              type="date"
              aria-label="Start date"
              value={custom.start}
              max={custom.end}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
            />
            to
            <DateInput
              type="date"
              aria-label="End date"
              value={custom.end}
              min={custom.start}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
            />
          </DateRow>
        )}
        {invalidCustom && <ErrorText>Choose a start date on or before the end date.</ErrorText>}
        {error && <ErrorText>{error}</ErrorText>}
      </Panel>

      {SECTIONS.map((section) => (
        <Panel key={section.title} title={section.title} subtitle={section.subtitle}>
          <MetricGrid>
            {section.metrics.map((m) => (
              <Metric key={m.key}>
                {report || error ? (
                  <MetricValue>{formatMetric(report?.[m.key], m.format)}</MetricValue>
                ) : (
                  <Skeleton $w="50%" $h="24px" $radius="8px" />
                )}
                <MetricLabel>
                  {m.label}
                  {m.info && <InfoTip text={m.info} label={`About ${m.label}`} />}
                </MetricLabel>
              </Metric>
            ))}
          </MetricGrid>
        </Panel>
      ))}

      <Note>
        Reports use the same rules as your payouts: only memberships your studio has checked in,
        counted by the date each membership started or renewed, with dates in UTC. Visits count
        classes that took place within the dates you pick.
      </Note>
    </PageStack>
  );
};

export default ReportsPage;
